import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { EmptyCell, NameValueRecord, Typography } from 'shared/ui'
import { truthy } from 'shared/lib'
import type { ISendFromInfo } from 'entities/SendFrom'
import { type EnrollmentFilters } from 'widgets/ContactEnrollment'
import { EnrollmentPreview } from 'widgets/ContactEnrollment/ui/EnrollmentList'
import { EnrollByWebhookCopyInput } from 'widgets/EnrollByWebhook/EnrollByWebhookCopyInput.tsx'
import { useDetailsContext } from '../detailsContext'
import styles from './styles.module.scss'

export const TriggerTab = observer(() => {
  const {
    data,
    futureSendFrom,
    futureFutureContactsCount,
    futureEnrollmentFilters,
    futureEnrollmentExcludeFilters,
  } = useDetailsContext()

  const [sendFrom, setSendFrom] = useState<ISendFromInfo | null>(null)
  const [contactsCount, setContactsCount] = useState<number | null>(null)
  const [enrollmentFilters, setEnrollmentFilters] = useState<EnrollmentFilters | null>(null)
  const [enrollmentExcludeFilters, setEnrollmentExcludeFilters] =
    useState<EnrollmentFilters | null>(null)

  useEffect(() => {
    futureSendFrom.then(setSendFrom)
    futureFutureContactsCount.then(setContactsCount)
    futureEnrollmentFilters?.then(setEnrollmentFilters)
    futureEnrollmentExcludeFilters?.then(setEnrollmentExcludeFilters)
  }, [])

  const filtersSize = data.filters
    ? Object.values(data.filters).reduce((sum, arr) => sum + (arr?.length ?? 0), 0)
    : 0

  const excludeFiltersSize = data.exclude_filters
    ? Object.values(data.exclude_filters).reduce((sum, arr) => sum + (arr?.length ?? 0), 0)
    : 0

  const hasEnrollment = filtersSize > 0 || excludeFiltersSize > 0 || data.enroll_by_webhook
  const runValue = [
    'Manually',
    (filtersSize > 0 || excludeFiltersSize > 0) && 'Automatically',
    data.enroll_by_webhook && 'By webhook',
  ]
    .filter(truthy)
    .join(', ')
  const reEnroll = data.settings?.allow_re_entering?.value

  return (
    <div className={styles.section}>
      <Typography variant={'body-lg-medium'} ariaLabel={'Trigger'}>
        Trigger
      </Typography>
      <div className={styles.sectionRows}>
        <div className={styles.sectionRow}>
          <Typography ariaLabel='Run' variant='text-input-field' className={styles.label}>
            Run
          </Typography>
          <Typography ariaLabel='RunValue' variant='body-md-regular' className={styles.value}>
            {runValue}
          </Typography>
        </div>
        <div className={styles.sectionRow}>
          <Typography
            ariaLabel='EnrolledContacts'
            variant='text-input-field'
            className={styles.label}
          >
            Enrolled contacts
          </Typography>
          <Typography
            ariaLabel='EnrolledContactsValue'
            variant='body-md-regular'
            className={styles.value}
          >
            {contactsCount !== null ? contactsCount.toLocaleString() : 0}
          </Typography>
        </div>
        {hasEnrollment && (
          <>
            {filtersSize > 0 && (
              <div className={styles.sectionRow}>
                <Typography
                  ariaLabel='EnrollmentCriteria'
                  variant='text-input-field'
                  className={styles.label}
                >
                  Enrollment criteria
                </Typography>
                <div className={classNames(styles.value, { [styles.small]: !!enrollmentFilters })}>
                  {enrollmentFilters ? (
                    <div className={styles.enrollmentCriteria}>
                      <EnrollmentPreview filters={enrollmentFilters} />
                    </div>
                  ) : (
                    <EmptyCell />
                  )}
                </div>
              </div>
            )}
            {excludeFiltersSize > 0 && (
              <div className={styles.sectionRow}>
                <Typography
                  ariaLabel='ExcludeCriteria'
                  variant='text-input-field'
                  className={styles.label}
                >
                  Exclude criteria
                </Typography>
                <div
                  className={classNames(styles.value, {
                    [styles.small]: !!enrollmentExcludeFilters,
                  })}
                >
                  {enrollmentExcludeFilters ? (
                    <div className={styles.enrollmentCriteria}>
                      <EnrollmentPreview filters={enrollmentExcludeFilters} />
                    </div>
                  ) : (
                    <EmptyCell />
                  )}
                </div>
              </div>
            )}
            <div className={styles.sectionRow}>
              <Typography ariaLabel='ReEnroll' variant='text-input-field' className={styles.label}>
                Re-enroll
              </Typography>
              <Typography
                ariaLabel='ReEnrollValue'
                variant='body-md-regular'
                className={styles.value}
              >
                {reEnroll ? 'ON' : 'OFF'}
              </Typography>
            </div>
            <div className={styles.sectionRow}>
              <Typography
                ariaLabel='WebhookURL'
                variant='text-input-field'
                className={styles.label}
              >
                Webhook URL
              </Typography>
              {data.enroll_by_webhook &&
                (data.webhook_url ? (
                  <div className={styles.valueWrap}>
                    <EnrollByWebhookCopyInput
                      webhookUrl={data.webhook_url}
                      textFieldSize={'small'}
                      copyBtnSize={'medium'}
                    />
                  </div>
                ) : (
                  <Typography
                    ariaLabel='Available'
                    variant='body-md-regular'
                    className={styles.value}
                  >
                    Available after activation
                  </Typography>
                ))}
            </div>
          </>
        )}
        <div className={styles.sectionRow}>
          <Typography ariaLabel='SendFrom' variant='text-input-field' className={styles.label}>
            Send from
          </Typography>
          <div className={styles.value}>
            {sendFrom ? <NameValueRecord {...{ ...sendFrom, showIcon: false }} /> : <EmptyCell />}
          </div>
        </div>
      </div>
    </div>
  )
})
